/**
 * DateRangePicker — accessible native-input fallback date range picker.
 *
 * a11y:
 *   - Uses real `<input type="date">` pair with visible labels so screen
 *     readers and mobile browsers use their native pickers.
 *   - Reflows at 400% zoom without clipping (WCAG 1.4.10) by relying on
 *     the CSS `.rrm-daterange` flex container which wraps.
 */

import type { CustomDateRange } from '../types';

export interface DateRangePickerProps {
  value: CustomDateRange | null;
  onChange: (range: CustomDateRange) => void;
}

export function DateRangePicker({ value, onChange }: DateRangePickerProps): JSX.Element {
  const from = value?.from ?? '';
  const to = value?.to ?? '';
  return (
    <span className="rrm-daterange">
      <span aria-hidden>📅</span>
      <label style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
        <span style={{ fontSize: 10, color: 'var(--ink-500)', textTransform: 'uppercase', fontWeight: 600 }}>From</span>
        <input
          type="date"
          value={from}
          onChange={(e) => onChange({ from: e.target.value, to })}
          aria-label="Custom range start date"
        />
      </label>
      <span aria-hidden>→</span>
      <label style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
        <span style={{ fontSize: 10, color: 'var(--ink-500)', textTransform: 'uppercase', fontWeight: 600 }}>To</span>
        <input
          type="date"
          value={to}
          onChange={(e) => onChange({ from, to: e.target.value })}
          aria-label="Custom range end date"
          min={from || undefined}
        />
      </label>
    </span>
  );
}
